import Observer from '@capsule/observer'
import TrackElements from '@capsule/track-elements'

export default class CompanionSlide {
	companionSlide: HTMLElement | null
	slideshowValue: number
	companionSlideTarget: HTMLElement | null
	btnShowMore: HTMLElement | null
	btnShowMoreContainer: HTMLElement | null
	slideShowId: string | null
	baseUrl: string | null
	offset: number
	limit: number
	url: string
	trackElements: TrackElements | null

	constructor() {
		this.companionSlide = document.getElementById('companionSlide')
		this.slideshowValue = parseInt(
			this.companionSlide?.getAttribute('data-slideshow-value') ?? '0',
			10
		)
		this.companionSlideTarget = document.getElementById('companionSlide-contentTarget')
		this.btnShowMore = document.getElementById('companionSlide-showMore')
		this.btnShowMoreContainer = document.getElementById('companionSlide-showMoreContainer')
		this.slideShowId = this.companionSlide?.getAttribute('data-slideshow-id') ?? null
		this.baseUrl = window.location.origin
		this.offset = 60
		this.limit = 20
		this.url = `${this.baseUrl}/_xhr/companionSlide/${this.slideShowId}?offset=${this.offset}&limit=${this.limit}`
		this.trackElements = null
	}

	init() {
		if (!this.slideShowId || !this.baseUrl) return

		this.observeInitialSlideBatchs()

		if (this.btnShowMore) {
			this.addEvent()
			this.addEventTracking()
		}
	}

	addEvent() {
		this.btnShowMore?.addEventListener('click', () => {
			this.toggleIsPending()
			this.showMore()
		})
	}

	addEventTracking() {
		this.trackElements = new TrackElements([
			{
				element: this.btnShowMore,
				display: {
					event: 'button_display',
					button_name: 'cta-more-diaporama',
					button_label: 'decouvrez-en-images'
				}
			},
			{
				element: this.btnShowMore,
				click: {
					event: 'button_click',
					button_name: 'cta-more-diaporama',
					button_label: 'decouvrez-en-images'
				}
			}
		])
		this.trackElements.init()
	}

	showMore() {
		this.insertNextSlides()

		// Prepare data for the next fetch
		this.offset += this.limit
		this.url = `${this.baseUrl}/_xhr/companionSlide/${this.slideShowId}?offset=${this.offset}&limit=${this.limit}`
	}

	async insertNextSlides() {
		try {
			const companionSlideBatchs = await this.getNextBatch()
			if (!companionSlideBatchs) return

			this.companionSlideTarget?.insertAdjacentHTML('beforeend', companionSlideBatchs)

			const newBatches = this.companionSlideTarget?.querySelectorAll(
				'.companionSlideBatch[data-xhr="true"]'
			)
			if (!newBatches?.length) return

			this.checkHasNextPage()

			// Inject ads and mark as processed
			for (const batch of newBatches) {
				this.injectAds(batch)
				batch.setAttribute('data-xhr', 'false')
			}

			this.toggleIsPending()
		} catch (error) {
			console.error('CompanionSlide::fetchData - Error: ', error)
		}
	}

	async getNextBatch() {
		const response = await fetch(this.url, {
			headers: { Accept: 'text/html' },
			credentials: 'same-origin'
		})

		if (!response.ok) return null
		return response.text()
	}

	checkHasNextPage() {
		if (this.offset >= (this.slideshowValue ?? 0)) {
			return this.removeBtnShowMore()
		}
	}

	/**
	 * Inject ads in the companionSlideBatch
	 * @param wrapper
	 */
	injectAds(wrapper: Element) {
		const ads = wrapper.querySelectorAll('.ad-lazyload')

		for (const ad of ads) {
			window.coreAds.queue.push(() => {
				window.coreAds.addDisplaySlot(ad.id)
			})
		}
	}

	removeBtnShowMore() {
		this.btnShowMoreContainer?.remove()
	}

	toggleIsPending() {
		this.btnShowMore?.classList.toggle('isPending')
	}

	observeInitialSlideBatchs() {
		if (!this.companionSlideTarget) return

		const [_firstBatch, ...initialSlideBatchs] = [
			...this.companionSlideTarget.querySelectorAll('.companionSlideBatch')
		]

		this.observeSlideBatchsSequentially(initialSlideBatchs)
	}

	async observeSlideBatchsSequentially(slideBatchs: Element[]) {
		for (const slideBatch of slideBatchs) {
			await this.observeSingleSlideBatch(slideBatch)
		}
	}

	observeSingleSlideBatch(slideBatch: Element): Promise<void> {
		return new Promise((resolve) => {
			const observer = new Observer({
				elements: slideBatch,
				onIntersection: (entry) => {
					const adsToLoad = entry.target.querySelectorAll('.ad-lazyload')
					for (const ad of adsToLoad) {
						window.coreAds.queue.push(() => {
							window.coreAds.addDisplaySlot(ad.id)
						})
					}
					resolve()
				}
			})

			observer.observe()
		})
	}
}
